import { IColumn, IDetailsRowProps } from '@fluentui/react';
import {
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesBasic,
  EmptyMessage,
  highlightRowBorder,
  IDetailsListProps,
  ListRow,
  listRowStylesThin,
  Loader,
  loaderStylesSpinnerXLarge,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  FeatureSet,
  GetFeatureSetPreviewResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import {
  featureSetPreviewStylesDefault,
  IFeatureSetPreviewClassNames,
  IFeatureSetPreviewStyles,
} from './FeatureSetPreview.styles';

const convertToListData = (result: Record<string, string>[]) => {
  const columns: IColumn[] = Object.keys(result[0]).map((col) => ({
    key: col,
    name: col,
    fieldName: col,
    minWidth: 100,
    maxWidth: 250,
  }));

  const items: { [key: string]: string }[] = result.length > 0 ? result : [];

  return {
    columns,
    items,
  };
};

const getAPI = (
  featureSetVersion: string | undefined,
  featureSetId: string | undefined,
  reviewId: string | undefined,
  title?: ContentsTitle
) => {
  switch (title) {
    case ContentsTitle.pendingReviews:
      return fsApi(CoreService.GetFeatureSetPreviewToReview, {
        reviewId,
      });
    case ContentsTitle.pendingFeatureSets:
      return fsApi(CoreService.GetFeatureSetPreviewInReview, {
        reviewId,
      });
    default:
      return fsApi(CoreService.GetFeatureSetPreview, {
        featureSetId,
        featureSetVersion,
      });
  }
};

const parseTextToJson = (resultText: string) =>
  resultText
    .replace(/^"(.*)"$/, '$1')
    .replace(/\\n/g, '\n')
    .trim()
    .split(/\n/g)
    .map((value) => JSON.parse(`${value}`));

interface IFeatureSetPreviewProps {
  title?: ContentsTitle;
  reviewId?: string;
  featureSet?: FeatureSet;
  onUpdateData?: (count: number) => void;
}

export const FeatureSetPreview = ({ title, reviewId, featureSet, onUpdateData }: IFeatureSetPreviewProps) => {
  const [data, setData] = useState<{ columns: IColumn[]; items: { [key: string]: string }[] }>({
    columns: [],
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState('');
  const { addToast } = useToast();
  const load = useCallback(
    async (featureSetId = '', featureSetVersion = '') => {
      setLoadingMessage(`Loading feature preview...`);
      try {
        const previewResponse: GetFeatureSetPreviewResponse = await getAPI(
          featureSetVersion,
          featureSetId,
          reviewId,
          title
        );
        if (previewResponse?.previewUrl) {
          const result = await fetch(previewResponse.previewUrl!);
          const resultText = await result.text();
          if (resultText.length > 0) {
            const resultJson = parseTextToJson(resultText);
            setData(convertToListData(resultJson));
            onUpdateData && onUpdateData(resultJson.length);
          } else {
            onUpdateData && onUpdateData(0);
          }
        } else {
          onUpdateData && onUpdateData(0);
        }
      } catch (err: any) {
        if (err.message === 'Unexpected end of JSON input') {
          onUpdateData && onUpdateData(0);
        } else {
          addToast(getErrorMessageBarMessage(err, `An error has occurred while loading feature set preview: `));
        }
      } finally {
        setLoadingMessage('');
      }
    },
    [addToast, onUpdateData, title, reviewId]
  );
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesBasic,
    headerStyles: detailsHeaderStylesBasic,
    columns: data.columns,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return (
        <ListRow
          styles={listRowStylesThin}
          hideHeader
          data={item}
          columns={columns}
          onMouseEnter={(e) => highlightRowBorder(e)}
          onMouseLeave={(e) => highlightRowBorder(e, false)}
        />
      );
    },
  };
  useEffect(() => {
    if (featureSet) {
      load(featureSet.id, featureSet.version);
    }
  }, [load, featureSet]);
  const classNames = useClassNames<IFeatureSetPreviewStyles, IFeatureSetPreviewClassNames>(
    'FeatureSetPreview',
    featureSetPreviewStylesDefault
  );
  return (
    <div className={classNames.root} data-test="feature-set-preview">
      {loadingMessage && <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />}
      <div className={classNames.listView}>
        <div className={classNames.listViewWrap} data-is-scrollable="true">
          {data.items.length === 0 ? (
            <EmptyMessage message="No Items Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      </div>
    </div>
  );
};
